import { forwardRef, useEffect, useId, useMemo, useState } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  X,
  Check,
} from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/shared/lib/cn";
import {
  openDialogPortalContainer,
  useDialogPortalContainer,
} from "@/shared/ui/core/Popover";
import { useFormFieldContext } from "@/shared/ui/core/formFieldContext";
import {
  controlHeightClass,
  errorClass,
  fieldBorderClass,
  fieldFocusClass,
  hintClass,
  labelClass,
} from "./styles";
import type { ControlSize, FieldTone } from "./types";
import {
  MONTH_NAMES,
  WEEK_DAYS,
  parseInputDate,
  formatDateDisplay,
  formatDateOutput,
  buildCalendarGrid,
  isDayOutOfRange,
} from "./datePickerUtils";

export interface DatePickerProps {
  label?: string;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  size?: ControlSize;
  mode?: "date" | "datetime";
  showTime?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  clearable?: boolean;
  id?: string;
  className?: string;
  rootClassName?: string;
  "aria-label"?: string;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  function DatePicker(
    {
      label,
      hint,
      error,
      tone,
      size = "md",
      mode = "date",
      showTime: propShowTime,
      value: propValue,
      defaultValue,
      onChange,
      placeholder,
      minDate,
      maxDate,
      disabled,
      clearable = true,
      id,
      className,
      rootClassName,
      "aria-label": ariaLabel,
    },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const popoverContentId = `${fieldId}-calendar-popover`;
    const formField = useFormFieldContext();
    const effectiveError =
      error || (formField.inFormField ? formField.error : undefined);
    const resolvedTone: FieldTone =
      tone ?? (effectiveError ? "error" : "default");
    const shouldRenderError = Boolean(
      effectiveError &&
      (!formField.inFormField || (!formField.error && Boolean(error))),
    );
    const isDateTime = mode === "datetime" || Boolean(propShowTime);

    // Controlled or uncontrolled value
    const [internalValue, setInternalValue] = useState<string>(
      defaultValue ?? "",
    );
    const currentValue = propValue !== undefined ? propValue : internalValue;

    const parsed = useMemo(() => parseInputDate(currentValue), [currentValue]);
    const selectedDate = parsed?.date ?? null;

    // View state: Month & Year currently browsed
    const [viewDate, setViewDate] = useState<Date>(
      () => selectedDate ?? new Date(),
    );

    // Selected time state (HH:mm)
    const [selectedHour, setSelectedHour] = useState<number>(() =>
      selectedDate ? selectedDate.getHours() : 9,
    );
    const [selectedMinute, setSelectedMinute] = useState<number>(() =>
      selectedDate ? selectedDate.getMinutes() : 0,
    );

    const [open, setOpen] = useState(false);

    // Sync viewDate when selectedDate changes externally
    useEffect(() => {
      if (selectedDate) {
        setViewDate(
          new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
        );
        setSelectedHour(selectedDate.getHours());
        setSelectedMinute(selectedDate.getMinutes());
      }
    }, [currentValue]);

    // Global coordinator: close when any select/picker opens
    useEffect(() => {
      const handler = (e: Event) => {
        const customEvent = e as CustomEvent<{ id: string }>;
        if (customEvent.detail?.id !== fieldId) {
          setOpen(false);
        }
      };
      window.addEventListener("tm-select-open", handler);
      return () => window.removeEventListener("tm-select-open", handler);
    }, [fieldId]);

    const handleOpenChange = (next: boolean) => {
      if (next) {
        window.dispatchEvent(
          new CustomEvent("tm-select-open", { detail: { id: fieldId } }),
        );
        if (selectedDate) {
          setViewDate(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
          );
        }
      }
      setOpen(next);
    };

    // Capture-phase click-outside listener
    useEffect(() => {
      if (!open) return;

      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null;
        if (!target) return;

        const triggerEl = document.getElementById(fieldId);
        if (triggerEl && triggerEl.contains(target)) return;

        const contentEl = document.getElementById(popoverContentId);
        if (contentEl && contentEl.contains(target)) return;

        const wrapperEl = contentEl?.closest(
          "[data-radix-popper-content-wrapper]",
        );
        if (wrapperEl && wrapperEl.contains(target)) return;

        handleOpenChange(false);
      };

      document.addEventListener("pointerdown", handlePointerDown, true);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
      };
    }, [open, fieldId, popoverContentId]);

    // Navigate months
    const prevMonth = (e: React.MouseEvent) => {
      e.stopPropagation();
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const nextMonth = (e: React.MouseEvent) => {
      e.stopPropagation();
      setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // 42 cells for calendar grid
    const calendarDays = useMemo(() => buildCalendarGrid(viewDate), [viewDate]);

    // Commit change
    const emitDate = (
      d: Date,
      hour = selectedHour,
      minute = selectedMinute,
    ) => {
      const updated = new Date(d);
      if (isDateTime) {
        updated.setHours(hour, minute, 0, 0);
      } else {
        updated.setHours(0, 0, 0, 0);
      }
      const output = formatDateOutput(updated, isDateTime);
      if (propValue === undefined) {
        setInternalValue(output);
      }
      onChange?.(output);
      if (!isDateTime) {
        handleOpenChange(false);
      }
    };

    const handleSelectDay = (d: Date) => {
      if (isDayOutOfRange(d, minDate, maxDate)) return;
      emitDate(d);
    };

    const handleApplyDateTime = () => {
      const target = selectedDate ?? new Date();
      emitDate(target, selectedHour, selectedMinute);
      handleOpenChange(false);
    };

    const handleClear = (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (propValue === undefined) {
        setInternalValue("");
      }
      onChange?.("");
      handleOpenChange(false);
    };

    const handleSelectToday = () => {
      const today = new Date();
      if (isDayOutOfRange(today, minDate, maxDate)) return;
      setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
      emitDate(today);
    };

    const todayStr = useMemo(() => {
      const now = new Date();
      return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
    }, []);

    const selectedStr = useMemo(() => {
      if (!selectedDate) return "";
      return `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`;
    }, [selectedDate]);

    const displayLabel = selectedDate
      ? formatDateDisplay(selectedDate, isDateTime)
      : "";

    const defaultPlaceholder = isDateTime
      ? "Chọn ngày & giờ..."
      : "Chọn ngày ...";

    return (
      <div className={cn("flex w-full flex-col gap-1", rootClassName)}>
        {label ? (
          <label htmlFor={fieldId} className={labelClass}>
            {label}
          </label>
        ) : null}

        <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
          <PopoverPrimitive.Trigger asChild>
            <button
              ref={ref}
              type="button"
              id={fieldId}
              disabled={disabled}
              aria-label={ariaLabel ?? label ?? defaultPlaceholder}
              aria-expanded={open}
              className={cn(
                "inline-flex w-full items-center justify-between gap-2 rounded-input border bg-surface px-3 text-left text-body font-sans text-text outline-none transition-colors cursor-pointer",
                fieldFocusClass,
                "disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-fg",
                !displayLabel && "text-text-muted",
                controlHeightClass[size],
                fieldBorderClass[resolvedTone],
                className,
              )}
            >
              <div className="flex items-center min-w-0">
                <span
                  className={cn(
                    "truncate",
                    displayLabel ? "font-mono" : "font-sans",
                  )}
                >
                  {displayLabel || placeholder || defaultPlaceholder}
                </span>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {clearable && displayLabel && !disabled ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClear}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleClear();
                      }
                    }}
                    aria-label="Xóa ngày đã chọn"
                    className="rounded p-0.5 text-text-muted hover:text-text hover:bg-bg transition-colors cursor-pointer"
                  >
                    <X className="size-4" />
                  </span>
                ) : (
                  <CalendarIcon
                    className="size-4 text-text-muted transition-colors hover:text-text"
                    aria-hidden
                  />
                )}
              </div>
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal container={useDialogPortalContainer()}>
            <PopoverPrimitive.Content
              id={popoverContentId}
              align="start"
              sideOffset={4}
              className={cn(
                "z-[var(--z-dropdown)] w-[288px] overflow-hidden rounded-control border border-border bg-surface shadow-2xl p-3 outline-none animate-in fade-in-50 zoom-in-95 duration-150",
                isDateTime && "w-[304px]",
              )}
            >
              {/* 1. Month / Year Navigation */}
              <div className="flex items-center justify-between pb-2 border-b border-border/60">
                <button
                  type="button"
                  onClick={prevMonth}
                  className="flex h-[28px] w-[28px] items-center justify-center rounded-md border border-border bg-surface text-text hover:bg-bg transition-colors cursor-pointer"
                  aria-label="Tháng trước"
                >
                  <ChevronLeft className="size-3.5" />
                </button>

                <div className="font-semibold text-[13px] text-text">
                  {MONTH_NAMES[viewDate.getMonth()]}, {viewDate.getFullYear()}
                </div>

                <button
                  type="button"
                  onClick={nextMonth}
                  className="flex h-[28px] w-[28px] items-center justify-center rounded-md border border-border bg-surface text-text hover:bg-bg transition-colors cursor-pointer"
                  aria-label="Tháng sau"
                >
                  <ChevronRight className="size-3.5" />
                </button>
              </div>

              {/* 2. Days of week row */}
              <div className="grid grid-cols-7 gap-1 pt-2 pb-1 text-center font-semibold text-[11px] text-text-muted">
                {WEEK_DAYS.map((wd, i) => (
                  <div key={wd} className={cn(i >= 5 && "text-warning-fg/80")}>
                    {wd}
                  </div>
                ))}
              </div>

              {/* 3. Days grid (42 days) */}
              <div className="grid grid-cols-7 gap-1 pt-0.5">
                {calendarDays.map(({ date, isCurrentMonth }, idx) => {
                  const dateStr = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                  const isSelected = selectedStr === dateStr;
                  const isToday = todayStr === dateStr;
                  const outOfRange = isDayOutOfRange(date, minDate, maxDate);

                  return (
                    <button
                      key={idx}
                      type="button"
                      disabled={outOfRange}
                      onClick={() => handleSelectDay(date)}
                      className={cn(
                        "flex h-[30px] w-full items-center justify-center rounded-md text-[12px] font-mono transition-colors",
                        outOfRange && "cursor-not-allowed text-text-muted/35",
                        !outOfRange && "cursor-pointer",
                        !outOfRange &&
                          !isCurrentMonth &&
                          "text-text-muted/40 hover:bg-bg/60",
                        !outOfRange &&
                          isCurrentMonth &&
                          !isSelected &&
                          "text-text hover:bg-primary-soft hover:text-primary",
                        !outOfRange &&
                          isToday &&
                          !isSelected &&
                          "border border-primary/50 text-primary font-bold",
                        !outOfRange &&
                          isSelected &&
                          "bg-primary text-on-primary font-bold shadow-xs hover:bg-primary-hover",
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* 4. Time Picker Section (If datetime mode) */}
              {isDateTime && (
                <div className="mt-2.5 pt-2.5 border-t border-border/60">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-text-muted">
                      <Clock className="size-3.5 text-primary" />
                      <span>Giờ:</span>
                    </div>

                    <div className="flex items-center gap-1 font-mono text-[12px]">
                      <select
                        value={selectedHour}
                        onChange={(e) =>
                          setSelectedHour(Number(e.target.value))
                        }
                        aria-label="Chọn giờ"
                        className="h-[28px] rounded border border-border bg-bg px-1.5 text-text focus:border-primary focus:outline-none cursor-pointer"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {String(i).padStart(2, "0")}
                          </option>
                        ))}
                      </select>
                      <span className="font-bold text-text-muted">:</span>
                      <select
                        value={selectedMinute}
                        onChange={(e) =>
                          setSelectedMinute(Number(e.target.value))
                        }
                        aria-label="Chọn phút"
                        className="h-[28px] rounded border border-border bg-bg px-1.5 text-text focus:border-primary focus:outline-none cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => i * 5).map(
                          (m) => (
                            <option key={m} value={m}>
                              {String(m).padStart(2, "0")}
                            </option>
                          ),
                        )}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={handleApplyDateTime}
                      className="inline-flex items-center gap-1 h-[28px] px-2.5 rounded bg-primary text-on-primary text-[11px] font-semibold hover:bg-primary-hover transition-colors shadow-2xs cursor-pointer ml-auto"
                    >
                      <Check className="size-3" />
                      <span>Xong</span>
                    </button>
                  </div>
                </div>
              )}

              {/* 5. Footer Quick Shortcuts */}
              <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-border/50 text-[11px]">
                <button
                  type="button"
                  onClick={handleClear}
                  className="font-medium text-text-muted hover:text-danger transition-colors cursor-pointer"
                >
                  Xóa chọn
                </button>
                <button
                  type="button"
                  onClick={handleSelectToday}
                  disabled={isDayOutOfRange(new Date(), minDate, maxDate)}
                  className="font-semibold text-primary hover:underline transition-colors cursor-pointer disabled:cursor-not-allowed disabled:text-text-muted disabled:no-underline"
                >
                  Hôm nay
                </button>
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>

        {shouldRenderError ? (
          <p id={`${fieldId}-error`} className={errorClass} role="alert">
            {effectiveError}
          </p>
        ) : hint ? (
          <p id={`${fieldId}-hint`} className={hintClass}>
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
